import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function Lesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [moduleData, setModuleData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/lessons/${lessonId}`);
      setData(data);
      const m = await api.get(`/curriculum/${data.module.id}`);
      setModuleData(m.data);
    })();
  }, [lessonId]);

  if (!data || !moduleData) return <div className="max-w-3xl mx-auto px-6 py-16 label-eyebrow">Loading lesson…</div>;

  const { lesson, module } = data;
  const idx = moduleData.lessons.findIndex((l) => l.id === lesson.id);
  const prev = idx > 0 ? moduleData.lessons[idx - 1] : null;
  const next = idx < moduleData.lessons.length - 1 ? moduleData.lessons[idx + 1] : null;
  const lastLesson = !next;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-0 min-h-[calc(100vh-64px)]" data-testid={`lesson-page-${lesson.id}`}>
      {/* Sidebar */}
      <aside className="border-r border-stone bg-bone p-8 lg:sticky lg:top-16 lg:h-[calc(100vh-64px)] lg:overflow-y-auto">
        <Link to={`/curriculum/${module.id}`} className="label-eyebrow inline-flex items-center gap-2 mb-6 hover:text-ink" data-testid="back-to-module">
          <ArrowLeft size={12} strokeWidth={1.5} /> Module {module.order}
        </Link>
        <div className="font-serif text-lg text-ink leading-tight mb-6">{module.title}</div>
        <ol className="space-y-3 text-sm">
          {moduleData.lessons.map((l, i) => (
            <li key={l.id}>
              <Link
                to={`/lesson/${l.id}`}
                className={`block leading-snug ${l.id === lesson.id ? "text-ink font-medium" : "text-muted-foreground hover:text-ink"}`}
                data-testid={`sidebar-lesson-${l.id}`}
              >
                <span className="text-stone font-mono mr-2">{String(i + 1).padStart(2, "0")}</span>
                {l.title}
              </Link>
            </li>
          ))}
        </ol>
      </aside>

      {/* Content */}
      <article className="bg-white">
        <div className="max-w-[68ch] mx-auto px-6 lg:px-12 py-16">
          <div className="label-eyebrow mb-4">{module.title} · Lesson {idx + 1} of {moduleData.lessons.length}</div>
          <h1 className="font-serif text-5xl text-ink leading-[1.1] mb-3">{lesson.title}</h1>
          <div className="text-sm text-muted-foreground mb-12">{lesson.minutes} min read</div>

          <div className="space-y-6 text-[1.05rem] leading-[1.85] text-foreground/90">
            {lesson.body.map((p, i) => (
              <p key={i} className={i === 0 ? "drop-cap" : ""}>{p}</p>
            ))}
          </div>

          <aside className="my-12 border-l-4 border-terracotta bg-cream/60 p-6">
            <div className="label-eyebrow mb-3">Key takeaways</div>
            <ul className="space-y-2 text-foreground/90">
              {lesson.key_takeaways.map((k, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-terracotta font-serif text-xl leading-none mt-1">·</span>
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </aside>

          {/* Nav */}
          <div className="flex items-center justify-between mt-16 pt-8 border-t border-stone">
            {prev ? (
              <Link to={`/lesson/${prev.id}`} className="btn-ghost" data-testid="lesson-prev-btn">
                <ArrowLeft size={14} strokeWidth={1.5} /> Previous
              </Link>
            ) : <span />}
            {lastLesson ? (
              <Link to={`/quiz/${moduleData.quiz_id}`} className="btn-primary" data-testid="lesson-to-quiz-btn">
                Take mastery quiz <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            ) : (
              <Link to={`/lesson/${next.id}`} className="btn-primary" data-testid="lesson-next-btn">
                Next lesson <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
